import * as React from 'react';
import { UploadSimple, X, Paperclip } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useT } from '@/i18n/LanguageContext';

/**
 * U8 — ブラウザ標準の「ファイルを選択／選択されていません」はOS/ブラウザの言語で
 * 出るため日本語にできない。ドラッグ＆ドロップ＋日本語のラベルを自前で出し、
 * 標準のダイアログはクリックしたときだけ開く（標準ボタン自体は隠す）。
 */
export default function FileDropField({
  onFiles, accept, multiple = false, disabled, hint, testid = 'file-drop', className,
}) {
  const { t } = useT();
  const inputRef = React.useRef(null);
  const [over, setOver] = React.useState(false);
  const [picked, setPicked] = React.useState([]);

  const take = (fileList) => {
    const files = [...(fileList || [])];
    if (!files.length) return;
    setPicked(files);
    onFiles?.(multiple ? files : files[0], files);
  };

  const clear = (e) => {
    e.stopPropagation();
    setPicked([]);
    if (inputRef.current) inputRef.current.value = '';
    onFiles?.(multiple ? [] : null, []);
  };

  return (
    <div className={className}>
      <button
        type="button"
        disabled={disabled}
        data-native-drop
        data-testid={testid}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled) take(e.dataTransfer.files); }}
        className={cn(
          'w-full rounded-lg border border-dashed px-4 py-4 text-center transition-colors',
          over ? 'border-indigo-400 bg-indigo-50/70' : 'border-zinc-300 bg-white hover:bg-zinc-50',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <UploadSimple size={20} weight="duotone" className="mx-auto mb-1 text-indigo-500" />
        <p className="text-xs font-medium text-zinc-700">{t('common.dropFile')}</p>
        {hint && <p className="mt-0.5 text-[11px] text-zinc-400">{hint}</p>}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => take(e.target.files)}
        data-testid={`${testid}-input`}
        className="hidden"
      />
      {picked.length > 0 && (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-1.5"
          data-testid={`${testid}-selected`}>
          <Paperclip size={13} className="text-zinc-400 shrink-0" />
          <span className="text-xs text-zinc-700 truncate flex-1">
            {picked.map((f) => f.name).join('、')}
          </span>
          <button type="button" onClick={clear} aria-label={t('common.clearFile')} title={t('common.clearFile')}
            data-testid={`${testid}-clear`} className="text-zinc-400 hover:text-rose-600">
            <X size={13} />
          </button>
        </div>
      )}
    </div>
  );
}
